/**
 * Monte-Carlo equity estimation.
 *
 * Given the bot's own hole cards and the board cards publicly visible so
 * far (as produced by `botViewFor`, i.e. a `BotView.board` array -- empty
 * preflop, `[JOKER, ...reals]` from the flop on), estimates the bot's
 * win probability by repeatedly sampling: (a) an opponent hole (unless a
 * fixed one is supplied), and (b) however many of the four real board
 * cards haven't been revealed yet, all drawn uniformly without
 * replacement from the cards neither player can see. Each sample is then
 * scored with `evaluateJoker`, which independently resolves the joker's
 * best interpretation for each player from their own 46-candidate
 * exclusion set (own hole + the four real board cards) -- callers don't
 * need to think about the joker at all beyond treating it as absent from
 * `visibleBoard`.
 */

import {
  evaluateJoker,
  fullDeck,
  type BoardReal,
  type Card,
  type Rng,
} from "@joker-holdem/core";
import { JOKER } from "./view.js";

export const DEFAULT_EQUITY_SAMPLES = 400;

export interface EquityOptions {
  readonly rng: Rng;
  /** Number of Monte-Carlo samples. Default 400. */
  readonly samples?: number;
  /**
   * If provided, the opponent's hole is treated as known/fixed instead of
   * sampled uniformly from the unseen deck. Useful for exact matchup
   * equity (e.g. symmetry tests: estimateEquity(a, board, {opponentHole: b})
   * + estimateEquity(b, board, {opponentHole: a}) ~= 1).
   */
  readonly opponentHole?: readonly [Card, Card];
}

/** Extracts the real (non-joker) cards from a BotView-style board array, preserving order. */
function realCardsOf(visibleBoard: readonly Card[]): Card[] {
  const out: Card[] = [];
  for (const c of visibleBoard) {
    if (c !== JOKER) out.push(c);
  }
  return out;
}

/**
 * Partial Fisher-Yates: randomizes `arr[0..k-1]` to a uniformly random
 * k-subset (in random order) of the full array's contents, using swaps
 * against the full array. Mutates `arr`. Safe to call repeatedly on the
 * same array across samples -- each call independently re-randomizes the
 * whole array from wherever it currently is, which is still a valid
 * uniform shuffle.
 */
function partialShuffleDraw(arr: Card[], k: number, rng: Rng): void {
  const n = arr.length;
  for (let i = 0; i < k && i < n; i++) {
    const j = i + rng.nextInt(n - i);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

/**
 * Estimates `myHole`'s equity (win probability, chops counted as 0.5)
 * against an opponent given the board revealed so far. `visibleBoard`
 * must be in `BotView.board` format: `[]` preflop, or `[JOKER, ...reals]`
 * from the flop onward.
 */
export function estimateEquity(
  myHole: readonly [Card, Card],
  visibleBoard: readonly Card[],
  opts: EquityOptions
): number {
  const { rng } = opts;
  const samples = opts.samples ?? DEFAULT_EQUITY_SAMPLES;
  if (samples <= 0) {
    throw new RangeError("estimateEquity: samples must be positive.");
  }

  const knownReal = realCardsOf(visibleBoard);
  const missingRealCount = 4 - knownReal.length;
  if (missingRealCount < 0) {
    throw new RangeError("estimateEquity: visibleBoard has more than 4 real cards.");
  }

  const excluded = new Set<Card>([myHole[0], myHole[1], ...knownReal]);
  if (opts.opponentHole) {
    excluded.add(opts.opponentHole[0]);
    excluded.add(opts.opponentHole[1]);
  }

  const remaining = fullDeck().filter((c) => !excluded.has(c));
  const drawCount = (opts.opponentHole ? 0 : 2) + missingRealCount;

  if (remaining.length < drawCount) {
    throw new RangeError("estimateEquity: not enough unseen cards to sample from.");
  }

  let wins = 0;
  let ties = 0;

  for (let i = 0; i < samples; i++) {
    partialShuffleDraw(remaining, drawCount, rng);

    let cursor = 0;
    let oppHole: readonly [Card, Card];
    if (opts.opponentHole) {
      oppHole = opts.opponentHole;
    } else {
      oppHole = [remaining[0]!, remaining[1]!];
      cursor = 2;
    }

    const boardReal = [
      ...knownReal,
      ...remaining.slice(cursor, cursor + missingRealCount),
    ] as unknown as BoardReal;

    const myValue = evaluateJoker(myHole, boardReal);
    const oppValue = evaluateJoker(oppHole, boardReal);

    if (myValue > oppValue) wins++;
    else if (myValue === oppValue) ties++;
  }

  return (wins + 0.5 * ties) / samples;
}
